import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { ChevronUp, ChevronDown, ChevronRight, Check, Trash2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "src/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import BorderedInput from "src/components/form-new/bordered-input";
import Checkbox from "src/components/form-new/checkbox";
import type {
  Referral,
  ReferralLine,
  ReferralInvoiceLineItem,
  ReferralLineItemOverride,
  CommissionInvoice,
  CommissionReport,
} from "../types/commissions.types";
import {
  useUpdateCommissionReferralMutation,
  useCreateCommissionReferralLineMutation,
  useUpdateCommissionReferralLineMutation,
  useDeleteCommissionReferralLineMutation,
  useGetCommissionReportsQuery,
  useUpdateCommissionReportMutation,
} from "../endpoints/commissions.endpoints";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { formatCurrency } from "../../utils/billing-utils";

const MONTH_OPTIONS = [
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

function getYearOptions(): { value: string; label: string }[] {
  const current = new Date().getFullYear();
  const options: { value: string; label: string }[] = [];
  for (let y = current - 5; y <= current + 2; y++) {
    options.push({ value: String(y), label: String(y) });
  }
  return options;
}

const YEAR_OPTIONS = getYearOptions();

function Label({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`text-sm font-medium text-gray-700 ${className ?? ""}`}>
      {children}
    </span>
  );
}

export interface InvoiceOption {
  value: string;
  label: string;
}

/** Local line with string rate for input; id used as React key; apiId = API line id when saved */
interface ReferralLineRow {
  id: string;
  apiId?: string;
  companyId: string;
  invoiceId: string;
  rate: string;
  isRecurring: boolean;
  /** When not recurring: month 1–12 (empty string when unset) */
  month: string;
  /** When not recurring: year (empty string when unset) */
  year: string;
}

export interface ReferralDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  referral: Referral | null;
  isNew: boolean;
  onSave: (referral: Partial<Referral> & { id?: string }) => void;
  /** Options for company select per line (from b2b/billing/companies). */
  companyOptions?: InvoiceOption[];
  /** Map company id (select value) to ext_customer_id for the referral line API. */
  companyIdToExtCustomerId?: Record<string, string>;
  /** Options for invoice select per line (e.g. from commission invoices). */
  invoiceOptions?: InvoiceOption[];
  /** Invoice line items by invoice ID for the Edit line expansion (optional; uses mock items when missing). */
  invoiceLineItemsByInvoiceId?: Record<string, ReferralInvoiceLineItem[]>;
  /** All commission invoices (for expand: show invoices and lines by company). */
  commissionInvoices?: CommissionInvoice[];
}

/** Default mock line items when no invoice line items are provided for an invoice */
function getMockLineItemsForInvoice(_invoiceId: string): ReferralInvoiceLineItem[] {
  return [
    { id: "li-1", description: "Line item 1" },
    { id: "li-2", description: "Line item 2" },
    { id: "li-3", description: "Line item 3" },
  ];
}

const ReferralDrawer: React.FC<ReferralDrawerProps> = ({
  open,
  onOpenChange,
  referral,
  isNew,
  onSave,
  companyOptions = [],
  companyIdToExtCustomerId = {},
  invoiceOptions = [],
  invoiceLineItemsByInvoiceId = {},
  commissionInvoices = [],
}) => {
  // Single form for create and edit: name, email, referral lines (invoice + rate + recurring per line)
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [lines, setLines] = useState<ReferralLineRow[]>([]);
  const [expandedLineId, setExpandedLineId] = useState<string | null>(null);
  /** lineItemOverrides[rowId][lineItemId] = { rate, ignored } */
  const [lineItemOverrides, setLineItemOverrides] = useState<
    Record<string, Record<string, { rate: string; ignored: boolean }>>
  >({});
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [breakdownExpanded, setBreakdownExpanded] = useState(false);
  /** Editable commission value per report row id (for month-by-month breakdown) */
  const [editingCommissionByReportId, setEditingCommissionByReportId] = useState<Record<string, string>>({});
  const initialSnapshot = useRef<{
    name: string;
    email: string;
    linesJson: string;
    overridesJson: string;
  }>({ name: "", email: "", linesJson: "", overridesJson: "" });
  const initialLinesRef = useRef<Record<string, { companyId: string; rate: string; isRecurring: boolean; month: string; year: string }>>({});
  const initialLineItemOverridesRef = useRef<Record<string, Record<string, { rate: string; ignored: boolean }>>>({});

  const [updateReferral, { isLoading: isUpdatingReferral }] = useUpdateCommissionReferralMutation();
  const [createLine, { isLoading: isCreatingLine }] = useCreateCommissionReferralLineMutation();
  const [updateLineApi, { isLoading: isUpdatingLine }] = useUpdateCommissionReferralLineMutation();
  const [deleteReferralLine] = useDeleteCommissionReferralLineMutation();
  const [updateCommissionReport] = useUpdateCommissionReportMutation();

  const getLineItemsForInvoice = useCallback(
    (invoiceId: string): ReferralInvoiceLineItem[] => {
      if (!invoiceId.trim()) return [];
      const provided = invoiceLineItemsByInvoiceId[invoiceId];
      if (provided?.length) return provided;
      return getMockLineItemsForInvoice(invoiceId);
    },
    [invoiceLineItemsByInvoiceId]
  );

  const { data: allReferralReports = [] } = useGetCommissionReportsQuery(
    { type: "referral" },
    { skip: !open || !referral?.id || isNew }
  );
  const referralBreakdownRows = useMemo(() => {
    if (!referral?.id) return [];
    const id = referral.id;
    const filtered = allReferralReports.filter(
      (r) => (r.referralId ?? r.referral) === id
    );
    return [...filtered].sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month);
  }, [allReferralReports, referral?.id]);

  useEffect(() => {
    if (open) {
      setExpandedLineId(null);
      setShowDiscardConfirm(false);
      setBreakdownExpanded(false);
      setEditingCommissionByReportId({});
      if (referral) {
        const n = referral.name ?? referral.organizationName ?? "";
        const e = referral.email ?? referral.contactEmail ?? "";
        setName(n);
        setEmail(e);
        const fromLines = referral.referralLines ?? referral.lines;
        if (fromLines?.length) {
          const newLines = fromLines.map((ln, i) => {
            const lineId = ln.id ?? `line-${i}`;
            return {
              id: lineId,
              apiId: ln.id,
              companyId: ln.companyId ?? "",
              invoiceId: ln.invoiceId ?? "",
              rate: String(ln.rate ?? ""),
              isRecurring: ln.isRecurring ?? false,
              month: ln.month != null ? String(ln.month) : "",
              year: ln.year != null ? String(ln.year) : "",
            };
          });
          setLines(newLines);
          initialLinesRef.current = newLines.reduce(
            (acc, l) => {
              acc[l.id] = { companyId: l.companyId, rate: l.rate, isRecurring: l.isRecurring, month: l.month, year: l.year };
              if (l.apiId) acc[l.apiId] = acc[l.id];
              return acc;
            },
            {} as Record<string, { companyId: string; rate: string; isRecurring: boolean; month: string; year: string }>
          );
          const overrides: Record<string, Record<string, { rate: string; ignored: boolean }>> = {};
          fromLines.forEach((ln, i) => {
            const rowId = ln.id ?? `line-${i}`;
            overrides[rowId] = {};
            if (ln.lines?.length) {
              ln.lines.forEach((item) => {
                overrides[rowId][item.id] = {
                  rate: String(item.rate),
                  ignored: false,
                };
              });
            }
            if (ln.lineItemOverrides?.length) {
              ln.lineItemOverrides.forEach((o) => {
                overrides[rowId][o.lineItemId] = {
                  rate: String(o.rate),
                  ignored: o.ignored,
                };
              });
            }
          });
          setLineItemOverrides(overrides);
          initialLineItemOverridesRef.current = JSON.parse(JSON.stringify(overrides));
          initialSnapshot.current = {
            name: n,
            email: e,
            linesJson: JSON.stringify(newLines.map((l) => ({ companyId: l.companyId, invoiceId: l.invoiceId, rate: l.rate, isRecurring: l.isRecurring, month: l.month, year: l.year }))),
            overridesJson: JSON.stringify(overrides),
          };
        } else if (referral.invoiceIds?.length || referral.rate != null) {
          const newLines = [
            {
              id: "line-0",
              companyId: "",
              invoiceId: referral.invoiceIds?.[0] ?? "",
              rate: referral.rate != null ? String(referral.rate) : "",
              isRecurring: referral.isRecurring ?? false,
              month: "",
              year: "",
            },
          ];
          initialLinesRef.current = {};
          setLines(newLines);
          setLineItemOverrides({});
          initialLineItemOverridesRef.current = {};
          initialSnapshot.current = {
            name: n,
            email: e,
            linesJson: JSON.stringify(newLines.map((l) => ({ companyId: l.companyId, invoiceId: l.invoiceId, rate: l.rate, isRecurring: l.isRecurring, month: l.month, year: l.year }))),
            overridesJson: "{}",
          };
        } else {
          const n = referral.name ?? referral.organizationName ?? "";
          const e = referral.email ?? referral.contactEmail ?? "";
          const newLines = [
            {
              id: "line-0",
              companyId: "",
              invoiceId: "",
              rate: String(referral.initialRatePercent ?? ""),
              isRecurring: false,
              month: "",
              year: "",
            },
          ];
          setLines(newLines);
          setLineItemOverrides({});
          initialLinesRef.current = {};
          initialLineItemOverridesRef.current = {};
          initialSnapshot.current = {
            name: n,
            email: e,
            linesJson: JSON.stringify(newLines.map((l) => ({ companyId: l.companyId, invoiceId: l.invoiceId, rate: l.rate, isRecurring: l.isRecurring, month: l.month, year: l.year }))),
            overridesJson: "{}",
          };
        }
      } else {
        setName("");
        setEmail("");
        setLines([]);
        setLineItemOverrides({});
        initialLinesRef.current = {};
        initialLineItemOverridesRef.current = {};
        initialSnapshot.current = { name: "", email: "", linesJson: "[]", overridesJson: "{}" };
      }
    }
  }, [open, referral, isNew]);

  const isDirty = (() => {
    const init = initialSnapshot.current;
    const linesJson = JSON.stringify(lines.map((l) => ({ companyId: l.companyId, invoiceId: l.invoiceId, rate: l.rate, isRecurring: l.isRecurring, month: l.month, year: l.year })));
    const overridesJson = JSON.stringify(lineItemOverrides);
    return name !== init.name || email !== init.email || linesJson !== init.linesJson || overridesJson !== init.overridesJson;
  })();

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && isDirty) {
        setShowDiscardConfirm(true);
        return;
      }
      onOpenChange(nextOpen);
    },
    [isDirty, onOpenChange]
  );

  const handleConfirmDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSave = () => {
    const referralLines: ReferralLine[] = lines
      .filter((row) => row.companyId.trim() !== "" || row.invoiceId.trim() !== "")
      .map((row) => {
        const rateNum = parseFloat(row.rate);
        const overrides = lineItemOverrides[row.id];
        const lineItemOverridesPayload: ReferralLineItemOverride[] | undefined =
          overrides && Object.keys(overrides).length > 0
            ? Object.entries(overrides).map(([lineItemId, o]) => ({
                lineItemId,
                rate: Number.isNaN(parseFloat(o.rate)) ? 0 : parseFloat(o.rate),
                ignored: o.ignored,
              }))
            : undefined;
        const monthNum = row.month.trim() ? parseInt(row.month, 10) : undefined;
        const yearNum = row.year.trim() ? parseInt(row.year, 10) : undefined;
        return {
          ...(row.companyId.trim() !== "" && { companyId: row.companyId.trim() }),
          invoiceId: row.invoiceId.trim(),
          rate: Number.isNaN(rateNum) ? 0 : rateNum,
          isRecurring: row.isRecurring,
          ...(!row.isRecurring && monthNum != null && !Number.isNaN(monthNum) && { month: monthNum }),
          ...(!row.isRecurring && yearNum != null && !Number.isNaN(yearNum) && { year: yearNum }),
          id: row.id,
          lineItemOverrides: lineItemOverridesPayload,
        };
      });
    const firstRate = referralLines[0]?.rate ?? 0;
    onSave({
      ...(referral?.id ? { id: referral.id } : {}),
      name: name.trim() || undefined,
      email: email.trim() || undefined,
      referralLines: referralLines.length ? referralLines : undefined,
      organizationName: name.trim() || undefined,
      contactEmail: email.trim() || undefined,
      initialRatePercent: firstRate,
      recurringRatePercent: firstRate,
    });
    onOpenChange(false);
  };

  const addLine = useCallback(() => {
    setLines((prev) => [
      ...prev,
      {
        id: `line-${Date.now()}`,
        companyId: "",
        invoiceId: "",
        rate: "",
        isRecurring: false,
        month: "",
        year: "",
      },
    ]);
  }, []);

  const isReferralDirty = !!(referral?.id && (name !== initialSnapshot.current.name || email !== initialSnapshot.current.email));
  const getCustomerName = useCallback((companyId: string) => companyOptions.find((c) => c.value === companyId)?.label ?? "", [companyOptions]);

  const getCompanyName = useCallback(
    (companyId: string) => companyOptions.find((c) => c.value === companyId)?.label ?? "",
    [companyOptions]
  );

  const getInvoicesForCompany = useCallback(
    (companyId: string): CommissionInvoice[] => {
      if (!companyId.trim()) return [];
      const companyName = getCompanyName(companyId);
      return commissionInvoices.filter(
        (inv) =>
          inv.extCustomerId === companyId ||
          (inv.companyName?.toLowerCase() === companyName?.toLowerCase())
      );
    },
    [commissionInvoices, getCompanyName]
  );

  const getLineItemName = useCallback(
    (companyId: string, lineItemId: string): string => {
      const invs = getInvoicesForCompany(companyId);
      for (const inv of invs) {
        const line = inv.lines?.find((l) => l.id === lineItemId);
        if (line) return line.name ?? "";
      }
      return "";
    },
    [getInvoicesForCompany]
  );

  const getInvoiceIdForLineItem = useCallback(
    (companyId: string, lineItemId: string): string => {
      const invs = getInvoicesForCompany(companyId);
      for (const inv of invs) {
        const line = inv.lines?.find((l) => l.id === lineItemId);
        if (line) return inv.id;
      }
      return "";
    },
    [getInvoicesForCompany]
  );

  const handleSaveReferralFields = useCallback(async () => {
    if (!referral?.id || !name.trim()) return;
    try {
      await updateReferral({ id: referral.id, name: name.trim(), email: email.trim() }).unwrap();
      initialSnapshot.current = { ...initialSnapshot.current, name: name.trim(), email: email.trim() };
    } catch {
      // error could be shown via toast
    }
  }, [referral?.id, name, email, updateReferral]);

  const isLineDirty = useCallback(
    (row: ReferralLineRow) => {
      if (!row.apiId) return false;
      const init = initialLinesRef.current[row.apiId] ?? initialLinesRef.current[row.id];
      const baseDirty = init
        ? row.companyId !== init.companyId ||
          row.rate !== init.rate ||
          row.isRecurring !== init.isRecurring ||
          row.month !== init.month ||
          row.year !== init.year
        : false;
      const overridesDirty =
        JSON.stringify(lineItemOverrides[row.id] ?? {}) !==
        JSON.stringify(initialLineItemOverridesRef.current[row.id] ?? {});
      return baseDirty || overridesDirty;
    },
    [lineItemOverrides]
  );

  const canSaveNewLine = useCallback((row: ReferralLineRow) => {
    const base = row.companyId.trim() !== "" && row.rate.trim() !== "" && !Number.isNaN(parseFloat(row.rate));
    if (row.isRecurring) return base;
    const monthOk = row.month.trim() !== "" && !Number.isNaN(parseInt(row.month, 10)) && parseInt(row.month, 10) >= 1 && parseInt(row.month, 10) <= 12;
    const yearOk = row.year.trim() !== "" && !Number.isNaN(parseInt(row.year, 10));
    return base && monthOk && yearOk;
  }, []);

  const handleSaveLine = useCallback(
    async (row: ReferralLineRow) => {
      if (!referral?.id) return;
      const customerName = getCustomerName(row.companyId);
      const rateNum = parseFloat(row.rate);
      const overrides = lineItemOverrides[row.id];
      const linesPayload =
        overrides && Object.keys(overrides).length > 0
          ? Object.entries(overrides).map(([id, o]) => ({
              id,
              name: getLineItemName(row.companyId, id),
              rate: Number.isNaN(parseFloat(o.rate)) ? 0 : parseFloat(o.rate),
              invoiceId: getInvoiceIdForLineItem(row.companyId, id) || undefined,
            }))
          : undefined;
      const monthNum = row.month.trim() ? parseInt(row.month, 10) : undefined;
      const yearNum = row.year.trim() ? parseInt(row.year, 10) : undefined;
      const payload = {
        rate: Number.isNaN(rateNum) ? 0 : rateNum,
        recurring: row.isRecurring,
        customer_name: customerName,
        ext_customer_id: companyIdToExtCustomerId[row.companyId] ?? row.companyId.trim(),
        ...(linesPayload && linesPayload.length > 0 && { lines: linesPayload }),
        ...(!row.isRecurring && monthNum != null && !Number.isNaN(monthNum) && { month: monthNum }),
        ...(!row.isRecurring && yearNum != null && !Number.isNaN(yearNum) && { year: yearNum }),
      };
      try {
        if (row.apiId) {
          await updateLineApi({ id: row.apiId, ...payload }).unwrap();
        } else {
          await createLine({ referral: referral.id, ...payload }).unwrap();
        }
        initialLinesRef.current[row.id] = { companyId: row.companyId, rate: row.rate, isRecurring: row.isRecurring, month: row.month, year: row.year };
        if (row.apiId) initialLinesRef.current[row.apiId] = initialLinesRef.current[row.id];
      } catch {
        // error could be shown via toast
      }
    },
    [referral?.id, getCustomerName, getLineItemName, getInvoiceIdForLineItem, companyIdToExtCustomerId, lineItemOverrides, createLine, updateLineApi]
  );

  const removeLine = useCallback(
    async (id: string) => {
      const row = lines.find((r) => r.id === id);
      if (row?.apiId) {
        try {
          await deleteReferralLine({ id: row.apiId }).unwrap();
        } catch {
          // Error could be shown via toast
        }
      }
      setLines((prev) => prev.filter((r) => r.id !== id));
    },
    [lines, deleteReferralLine]
  );

  const updateLine = useCallback((id: string, patch: Partial<ReferralLineRow>) => {
    setLines((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }, []);

  const getOrInitLineItemOverride = useCallback(
    (rowId: string, lineItemId: string, fallbackRate: string): { rate: string; ignored: boolean } => {
      const rowOverrides = lineItemOverrides[rowId];
      const existing = rowOverrides?.[lineItemId];
      if (existing) return existing;
      return { rate: fallbackRate, ignored: false };
    },
    [lineItemOverrides]
  );

  const updateLineItemOverride = useCallback(
    (rowId: string, lineItemId: string, patch: Partial<{ rate: string; ignored: boolean }>) => {
      setLineItemOverrides((prev) => {
        const rowOverrides = prev[rowId] ?? {};
        const current = rowOverrides[lineItemId] ?? { rate: "", ignored: false };
        const next = { ...rowOverrides, [lineItemId]: { ...current, ...patch } };
        return { ...prev, [rowId]: next };
      });
    },
    []
  );

  const toggleExpanded = useCallback((rowId: string) => {
    setExpandedLineId((prev) => (prev === rowId ? null : rowId));
  }, []);

  // Top: name/email row (obvious inputs, 50% each); then breakdown; then referral lines table
  const renderNameEmailRow = () => (
    <div className="flex gap-4 py-4 items-end">
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">Name</label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Jane Smith"
          className="border-gray-300 bg-white"
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">Email</label>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="e.g. billing@partner.com"
          className="border-gray-300 bg-white"
        />
      </div>
      {!isNew && isReferralDirty && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={isUpdatingReferral}
          onClick={handleSaveReferralFields}
          title="Save name and email"
        >
          <Check className="h-4 w-4 mr-1.5" />
          Save
        </Button>
      )}
    </div>
  );

  const renderReferralLinesTable = () => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label>Referral lines</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-md bg-gray-201 text-black"
          onClick={addLine}
        >
          + Add line
        </Button>
      </div>
      <div className="rounded-md border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-8" />
              <TableHead className="font-medium text-gray-700 min-w-[180px]">Company</TableHead>
              <TableHead className="font-medium text-gray-700 w-24">Rate (%)</TableHead>
              <TableHead className="font-medium text-gray-700 w-28">Month*</TableHead>
              <TableHead className="font-medium text-gray-700 w-24">Year*</TableHead>
              <TableHead className="font-medium text-gray-700 w-24">Recurring</TableHead>
              <TableHead className="font-medium text-gray-700 w-20 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lines.map((row) => {
              const isExpanded = expandedLineId === row.id;
              return (
                <React.Fragment key={row.id}>
                  <TableRow
                    className={`hover:bg-gray-50/80 ${!isNew && row.companyId.trim() ? "cursor-pointer" : ""} ${isExpanded ? "bg-gray-50" : ""}`}
                    onClick={(e) => {
                      if (!isNew && row.companyId.trim() && !(e.target as HTMLElement).closest("button, [role='menuitem'], input, [data-no-expand]")) {
                        toggleExpanded(row.id);
                      }
                    }}
                  >
                    <TableCell className="w-8 py-2 align-middle" onClick={(e) => e.stopPropagation()}>
                      {!isNew && row.companyId.trim() ? (
                        <button
                          type="button"
                          className="p-1 rounded hover:bg-gray-200"
                          onClick={() => toggleExpanded(row.id)}
                          aria-label={isExpanded ? "Collapse" : "Expand"}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      ) : null}
                    </TableCell>
                    <TableCell className="py-2 align-middle" data-no-expand>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-between font-normal border-gray-300 bg-white text-left min-w-[160px]"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="truncate">
                              {getCompanyName(row.companyId) || "Select company"}
                            </span>
                            <ChevronDown className="h-4 w-4 shrink-0 ml-1 opacity-50" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="max-h-[280px] overflow-y-auto">
                          {companyOptions.map((opt) => (
                            <DropdownMenuItem
                              key={opt.value}
                              onClick={(e) => {
                                e.stopPropagation();
                                updateLine(row.id, { companyId: opt.value });
                              }}
                            >
                              {opt.label || opt.value}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                    <TableCell className="py-2 align-middle" data-no-expand>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        value={row.rate}
                        onChange={(e) => updateLine(row.id, { rate: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                        className="h-8 w-20 border-gray-300 bg-white text-center"
                      />
                    </TableCell>
                    <TableCell className="py-2 align-middle" data-no-expand>
                      <Input
                        type="number"
                        min={1}
                        max={12}
                        placeholder="—"
                        value={row.month || ""}
                        onChange={(e) => updateLine(row.id, { month: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                        className="h-8 w-16 border-gray-300 bg-white text-center"
                        disabled={row.isRecurring}
                      />
                    </TableCell>
                    <TableCell className="py-2 align-middle" data-no-expand>
                      <Input
                        type="number"
                        min={2020}
                        max={2030}
                        placeholder="—"
                        value={row.year || ""}
                        onChange={(e) => updateLine(row.id, { year: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                        className="h-8 w-16 border-gray-300 bg-white text-center"
                        disabled={row.isRecurring}
                      />
                    </TableCell>
                    <TableCell className="py-2 align-middle" data-no-expand onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        value={row.isRecurring}
                        onChange={(checked) => updateLine(row.id, { isRecurring: checked })}
                        label="Recurring"
                      />
                    </TableCell>
                    <TableCell className="py-2 align-middle text-right" data-no-expand>
                      <div className="flex items-center justify-end gap-1">
                        {!isNew && (canSaveNewLine(row) || isLineDirty(row)) && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            disabled={isCreatingLine || isUpdatingLine}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveLine(row);
                            }}
                            title={row.apiId ? "Save line changes" : "Save new line"}
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeLine(row.id);
                          }}
                          title="Remove line"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {!isNew && isExpanded && (
                    <TableRow>
                      <TableCell colSpan={7} className="p-0 bg-white border-t border-gray-200">
                        <div className="px-3 py-3">
                          <Label className="text-gray-600 block mb-2">
                            {row.companyId.trim()
                              ? `Invoices and lines for ${getCompanyName(row.companyId) || "this company"}`
                              : "Invoices and lines"}
                          </Label>
                          {row.companyId.trim() ? (
                      (() => {
                        const companyInvoices = getInvoicesForCompany(row.companyId);
                        const rows: Array<{
                          invoice: CommissionInvoice;
                          line?: { id: string; sku: string; skuName?: string; name: string; amount: number };
                        }> = [];
                        companyInvoices.forEach((inv) => {
                          if (inv.lines?.length) {
                            inv.lines.forEach((line) =>
                              rows.push({
                                invoice: inv,
                                line: {
                                  id: line.id,
                                  sku: line.sku ?? "—",
                                  skuName: line.skuName,
                                  name: line.name ?? "—",
                                  amount: line.amount,
                                },
                              })
                            );
                          } else {
                            rows.push({ invoice: inv });
                          }
                        });
                        return rows.length > 0 ? (
                          <div className="rounded border border-gray-200 overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-gray-50">
                                  <TableHead className="font-medium text-gray-700 py-2 px-2">#</TableHead>
                                  <TableHead className="font-medium text-gray-700 py-2 px-2">Date</TableHead>
                                  <TableHead className="font-medium text-gray-700 py-2 px-2">Status</TableHead>
                                  <TableHead className="font-medium text-gray-700 py-2 px-2">SKU</TableHead>
                                  <TableHead className="font-medium text-gray-700 py-2 px-2 max-w-[10rem] w-[10rem]">Line item</TableHead>
                                  <TableHead className="font-medium text-gray-700 py-2 px-2 w-28 min-w-[7rem]">Rate (%)</TableHead>
                                  <TableHead className="font-medium text-gray-700 py-2 px-2 text-right">Amount</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {rows.map((r, idx) => (
                                  <TableRow key={r.line ? `${r.invoice.id}-${r.line.id}` : `${r.invoice.id}-${idx}`}>
                                    <TableCell className="py-2 px-2 text-gray-700">{r.invoice.invoiceNumber || "—"}</TableCell>
                                    <TableCell className="py-2 px-2 text-gray-600">
                                      {r.invoice.status === "paid" && r.invoice.paidDate ? r.invoice.paidDate : "—"}
                                    </TableCell>
                                    <TableCell className="py-2 px-2 capitalize">{r.invoice.status ?? "—"}</TableCell>
                                    <TableCell className="py-2 px-2 text-gray-700">{r.line?.skuName ?? r.line?.sku ?? "—"}</TableCell>
                                    <TableCell className="py-2 px-2 text-gray-700 max-w-[10rem] w-[10rem] truncate" title={r.line?.name ?? undefined}>{r.line?.name ?? "—"}</TableCell>
                                    <TableCell className="py-2 px-2 min-w-[7rem]">
                                      {r.line != null ? (
                                        <BorderedInput
                                          type="number"
                                          min={0}
                                          max={100}
                                          step={0.5}
                                          value={getOrInitLineItemOverride(row.id, r.line.id, row.rate).rate}
                                          onChange={(e) =>
                                            updateLineItemOverride(row.id, r.line!.id, { rate: e.target.value })
                                          }
                                          placeholder={row.rate || "0"}
                                          className="w-full min-w-[6rem] max-w-[8rem]"
                                        />
                                      ) : (
                                        "—"
                                      )}
                                    </TableCell>
                                    <TableCell className="py-2 px-2 text-right">
                                      {r.line != null ? formatCurrency(r.line.amount) : "—"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 py-2">No invoices found for this company.</p>
                        );
                      })()
                    ) : (
                      <p className="text-sm text-gray-500 py-2">Select a company to view invoices.</p>
                    )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
                );
              })}
            {lines.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-gray-500 py-6">
                  No lines yet. Add a line to associate a customer with a rate and recurring option.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <p className="text-xs text-gray-500 mt-2 px-0">
          * Month and year are only used for one-off referrals.
        </p>
      </div>
    </div>
  );

  return (
    <>
    <Sheet
      open={open}
      onOpenChange={handleOpenChange}
    >
      <SheetContent className="!w-[60vw] sm:!w-[75vw] !max-w-none overflow-y-auto bg-white">
        <SheetHeader>
          <SheetTitle>
            {isNew ? "Add Referral" : "Edit Referral"}
          </SheetTitle>
        </SheetHeader>
        {renderNameEmailRow()}
        {!isNew && referral?.id && (
          <div className="border border-gray-200 rounded-md mb-4">
            <Button
              type="button"
              variant="ghost"
              size="med"
              className="flex items-center gap-2 bg-gray-200 text-black w-full hover:bg-gray-300 rounded-md"
              onClick={() => setBreakdownExpanded((p) => !p)}
            >
              {breakdownExpanded ? (
                <ChevronUp className="h-4 w-4 shrink-0" />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0" />
              )}
              <span>Month-by-month commission breakdown</span>
              {referralBreakdownRows.length > 0 && (
                <span className="text-gray-500 font-normal">
                  ({referralBreakdownRows.length} month{referralBreakdownRows.length !== 1 ? "s" : ""})
                </span>
              )}
            </Button>
            {breakdownExpanded && (
              <div className="overflow-hidden bg-gray-50/50">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-medium text-gray-700">Month</TableHead>
                      <TableHead className="font-medium text-gray-700">Year</TableHead>
                      <TableHead className="font-medium text-gray-700 text-right w-1/4 min-w-[25%]">Commission</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referralBreakdownRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-gray-500 py-4 text-sm">
                          No commission data for this referral yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      referralBreakdownRows.map((row) => {
                        const rowKey = row.id ?? `report-${row.year}-${row.month}`;
                        const displayValue = editingCommissionByReportId[rowKey] ?? String(Number((row.commission ?? 0).toFixed(2)));
                        const handleCommissionBlur = () => {
                          if (!row.id) return;
                          const parsed = parseFloat(displayValue);
                          if (!Number.isNaN(parsed) && parsed !== (row.commission ?? 0)) {
                            updateCommissionReport({ id: row.id, commission: parsed }).unwrap().catch(() => {});
                          }
                          setEditingCommissionByReportId((prev) => {
                            const next = { ...prev };
                            delete next[rowKey];
                            return next;
                          });
                        };
                        return (
                          <TableRow key={rowKey} className="bg-white">
                            <TableCell className="text-sm">
                              {MONTH_OPTIONS.find((m) => m.value === String(row.month))?.label ?? row.month}
                            </TableCell>
                            <TableCell className="text-sm">{row.year}</TableCell>
                            <TableCell className="text-right text-sm font-medium w-1/4 min-w-[25%]">
                              {row.id ? (
                                <Input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  value={displayValue}
                                  onChange={(e) =>
                                    setEditingCommissionByReportId((prev) => ({ ...prev, [rowKey]: e.target.value }))
                                  }
                                  onBlur={handleCommissionBlur}
                                  className="h-8 w-full min-w-[6rem] max-w-[10rem] text-right border-gray-300 bg-white ml-auto"
                                />
                              ) : (
                                formatCurrency(Number((row.commission ?? 0).toFixed(2)))
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
        {renderReferralLinesTable()}
        <SheetFooter>
          {isNew && (
            <Button size="sm" onClick={handleSave}>
              Save
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
    <Dialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
      <DialogContent className="sm:max-w-md bg-white" onPointerDownOutside={e => e.preventDefault()}>
        <DialogTitle>Unsaved changes</DialogTitle>
        <p className="text-sm text-muted-foreground">
          You have unsaved changes. Are you sure you want to close? Your changes will be lost.
        </p>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" size="med" onClick={() => setShowDiscardConfirm(false)}>
            Stay
          </Button>
          <Button size="med" variant="delete" onClick={handleConfirmDiscard}>
            Discard changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ReferralDrawer;
